import { UserSwitchOutlined } from "@ant-design/icons";
import Typography from "antd/es/typography/Typography";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTaskListsThunk, getWorkGetScheduleThunk } from "tm-shared/api";
import { AnyTask, Point, TaskAssignment, TaskRoute } from "tm-shared/types";

import { ROLE_CHILDREN_MAP, ROUTES } from "@/constants";
import { TaskViewModalsContainer } from "@/features/TaskView";
import { useAsyncAction, useQueueInfo } from "@/hooks";
import { useGlobalCooldown } from "@/hooks/core/useGlobalCooldown";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { AssignmentFilter } from "@/pages/Assignments/AssignmentsPage/components/AssignmentFilter";
import { AssignmentPointList } from "@/pages/Assignments/AssignmentsPage/components/AssignmentPointList";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { HeaderNavLink } from "@/shared/navigation/HeaderNavLink";
import { HeaderReloadBtn } from "@/shared/navigation/HeaderReloadBtn";
import { isManagerRole } from "@/utils/business/isManagerRole";

import { AssignmentWithContext } from "./AssignmentPage.types";

const isAssignment = (t: AnyTask): t is TaskAssignment =>
    t.context.route === TaskRoute.CustomTask;

const AssignmentsPage = () => {
    const currentLocation = useRef("");
    const dispatch = useAppDispatch();
    const location = useLocation();

    const { disabled, trigger } = useGlobalCooldown(30_000);
    const { hasPending } = useQueueInfo();

    const tasks = useAppSelector((s) => s.work.tasks);
    const points = useAppSelector((s) => s.work.points);
    const offlineMode = useOfflineModeSelector();
    const isUnscheduledPointsProcessing = useAppSelector(
        (s) => s.offline.isUnscheduledPointsProcessing,
    );
    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);

    const isManager = isManagerRole(role);
    const [tasksInfoModal, setTasksInfoModal] = useState<{
        point: Point;
        task: AnyTask;
    } | null>(null);
    currentLocation.current = location.pathname?.split("/")[1] || "";

    const { filter: currentFilter } = useAppSelector(
        (s) => s.ui.assignments.page,
    );

    const assignmentsWithContext: AssignmentWithContext[] = useMemo(() => {
        return Object.entries(tasks).flatMap(([pointId, tMap]) => {
            const point = points[pointId];
            if (!point) return [];

            return Object.values(tMap)
                .filter(isAssignment)
                .map((t) => ({
                    assignment: t,
                    point,
                    allTasks: tMap,
                    id: t.id,
                }));
        });
    }, [points, tasks]);

    const filteredAssignmentsWithContext = useMemo(() => {
        if (!currentFilter) return assignmentsWithContext;

        return assignmentsWithContext.filter(
            (item) =>
                item.assignment.context.additionalData.assignmentType ===
                currentFilter,
        );
    }, [assignmentsWithContext, currentFilter]);

    const [handleSchedulesReload, schedulesReloadLoading] = useAsyncAction(
        async () => {
            trigger();
            const currentPointIds = Object.keys(points);
            const roleSlug = isManager ? ROLE_CHILDREN_MAP[role] : undefined;
            await dispatch(
                getWorkGetScheduleThunk({
                    userId: isManager ? activeSchedule?.userId : userId,
                    roleSlug,
                }),
            );
            await dispatch(
                getTaskListsThunk({ targetIds: currentPointIds, roleSlug }),
            );
        },
    );

    const handleOpenTasksInfoModal = (modal: {
        point: Point;
        task: AnyTask;
    }) => {
        setTasksInfoModal(modal);
    };

    const handleCloseTasksInfoModal = () => {
        setTasksInfoModal(null);
    };

    return (
        <>
            <Header
                titleLoading={schedulesReloadLoading}
                rightActions={
                    <>
                        <HeaderNavLink to={ROUTES.AssignmentsPage}>
                            <UserSwitchOutlined />
                        </HeaderNavLink>

                        <HeaderReloadBtn
                            onClick={handleSchedulesReload}
                            disabled={
                                disabled ||
                                offlineMode ||
                                hasPending ||
                                isUnscheduledPointsProcessing
                            }
                        />
                    </>
                }
            />

            <AssignmentFilter />
            <MainContainer>
                <div style={{ overflow: "auto" }}>
                    {filteredAssignmentsWithContext.length > 0 ? (
                        <AssignmentPointList
                            assignmentsWithContext={
                                filteredAssignmentsWithContext
                            }
                            onOpenTasksInfoModal={handleOpenTasksInfoModal}
                        />
                    ) : (
                        <Typography>Доручень не знайдено</Typography>
                    )}
                </div>
            </MainContainer>

            <TaskViewModalsContainer
                modal={tasksInfoModal}
                onClose={handleCloseTasksInfoModal}
            />
        </>
    );
};

export default AssignmentsPage;
